import React, { useEffect, useMemo, useState } from 'react';

import { TextInput } from '~/App/Components/Forms';
import { TextInputType } from '~/App/Components/Forms/TextInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import SearchResults from './SearchResults';
import { debounce, isEmpty } from 'lodash';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2';
import { Profile } from '~/types/models';
import NoResult from './NoResult';
import Loading from './Loading';
import { useProfiles } from '~/App/Api/Profile';
import useAnalytics from '~/react-hooks/use-analytics';
import { ADD_CONTACT_SEARCH, CREATE_CONTACT } from '~/generated/AnalyticsTypes';
import { Modal, ModalProps } from '@vimily/bonjoro-ui-components-v2/Modal';
import cx from 'classnames';

export interface SearchModalProps {
  heading: string;
  open?: boolean;
  loading?: boolean;
  className?: string;
  onModalClose: () => void;
  onContactAdd?: (contact: Profile) => void;
  onAddContactModalOpen?: () => void;
}

export default function SearchModal({
  heading,
  open = false,
  loading = false,
  className,
  onModalClose,
  onContactAdd,
  onAddContactModalOpen,
}: SearchModalProps) {
  const [query, setQuery] = useState('');
  //used to debounce the profile search
  const [searchQuery, setSearchQuery] = useState('');

  const analytics = useAnalytics();

  const { data: profiles = [], isLoading } = useProfiles({
    required: [],
    search: searchQuery,
  });

  const [selectedContact, setSelectedContact] = useState<Profile>();

  const updateSearchQuery = useMemo(() => debounce(setSearchQuery, 200), []);

  const hasSelectedContact = selectedContact !== undefined;

  const clearQuery = () => setQuery('');
  const clearSelectedContact = () => setSelectedContact(undefined);

  const onContactSelected = (contact: Profile) => {
    setSelectedContact(contact);
  };

  const handleOnRequestClose = () => onModalClose();

  const handleAddContactClick = () => {
    if (!hasSelectedContact) return;

    onContactAdd?.(selectedContact);
    handleOnRequestClose();
  };

  const handleAddContactModalOpen = () => {
    onAddContactModalOpen?.();

    analytics.track(CREATE_CONTACT);
  };

  useEffect(() => {
    clearSelectedContact();
    updateSearchQuery(query);
  }, [query]);

  useEffect(() => {
    if (!open) {
      clearQuery();
      clearSelectedContact();
    }
  }, [open]);

  const modalProps = useMemo<ModalProps>(() => {
    return {
      open,
      onModalClose,
      className: cx(className, 'max-w-[510px] !p-[30px]'),
      overlayClassName: '!z-[9950]',
      closeButtonClassName: '!w-5',
      hideActions: true,
      hideCloseButton: true,
    };
  }, [open]);

  return (
    <Modal {...modalProps}>
      {heading && (
        <h1 className='my-0 mx-auto text-xl font-bold v2-text-neutral-900 mb-5'>
          {heading}
        </h1>
      )}
      <div className='border border-solid border-neutral-300 rounded-lg focus-within:border-turquoise'>
        <FontAwesomeIcon
          icon={faSearch}
          className='left-5 top-1 relative text-neutral-300 text-xl'
        />
        <TextInput
          type={TextInputType.Text}
          placeholder='Type name or email address to search'
          className='!border-none !focus:border-none !w-[420px] !pl-[30px]'
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setQuery(e.target.value)
          }
          onClick={() => void analytics.track(ADD_CONTACT_SEARCH)}
        />
      </div>

      <div className='mt-5 border-solid border-0 border-y border-neutral-300'>
        <div className='overflow-y-auto h-72 scrollbar-hide'>
          {isLoading ? (
            <Loading />
          ) : (
            <SearchResults
              selectedContact={selectedContact}
              contacts={profiles}
              onContactSelected={onContactSelected}
            />
          )}
          {isEmpty(profiles) && !isLoading && <NoResult query={query} />}
        </div>
      </div>

      <div className='pt-5 flex flex-row justify-between'>
        <Button
          className='py-1.5'
          variant={ButtonVariant.Primary}
          onClick={handleAddContactModalOpen}
        >
          <i className='fa fa-plus' />
        </Button>
        <div className='flex flex-row gap-x-5'>
          <Button
            className='py-1.5'
            variant={ButtonVariant.Secondary}
            onClick={handleOnRequestClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className='py-1.5'
            variant={ButtonVariant.Primary}
            onClick={handleAddContactClick}
            disabled={!hasSelectedContact || loading}
          >
            Add contact
          </Button>
        </div>
      </div>
    </Modal>
  );
}
